import { StatusDot } from '@zentera/ui';
import { fmt } from './format.js';

/** Eventos do motor exibidos nos painéis de teste (Workspace) e no simulador (Admin). */
export type EngineEvent =
  | { type: 'retrieval'; chunks: { title: string; source: string; score: number }[] }
  | { type: 'llm_call'; provider?: string; model?: string; simulated?: boolean; inputTokens: number; outputTokens: number; latencyMs: number; profile: string }
  | { type: 'ai_failover' }
  | { type: 'tool'; name: string; decision: string; reason?: string; args: Record<string, unknown> }
  | { type: 'profile_change'; from: string; to: string }
  | { type: 'model_substituted'; aiProvider: string; from: string; to: string }
  | { type: 'model_fallback'; from: string; to: string; reason: string }
  | { type: 'handoff'; reason: string }
  | { type: 'guard'; action: string; reason: string }
  | { type: 'error'; message: string };


export const DECISION_TONE: Record<string, 'success' | 'danger' | 'warning' | 'muted' | 'info'> = {
  allowed: 'success', confirmed: 'success', denied: 'danger', error: 'danger', confirmation_required: 'warning', cancelled: 'muted',
};
export const DECISION_LABEL: Record<string, string> = {
  allowed: 'permitida', confirmed: 'confirmada', denied: 'negada', error: 'erro', confirmation_required: 'aguarda confirmação', cancelled: 'cancelada',
};
const FALLBACK_REASON: Record<string, string> = {
  model_unavailable: 'modelo indisponível',
  auth: 'credencial recusada',
  rate_limit: 'limite de uso do fornecedor',
  server: 'fornecedor instável',
  network: 'sem comunicação com o fornecedor',
  invalid_request: 'requisição recusada',
};

export const PROFILE_LABEL: Record<string, string> = { triage: 'Triagem', support: 'Suporte técnico', finance: 'Financeiro', sales: 'Comercial', general: 'Atendimento geral' };


export function TraceEvent({ e }: { e: EngineEvent }) {
  switch (e.type) {
    case 'retrieval':
      return (
        <>
          <div className="font-medium text-fg-2">Conhecimento consultado</div>
          {e.chunks.length ? (
            <ul className="mt-1 space-y-0.5 text-fg-3">
              {e.chunks.map((c, i) => <li key={i} className="truncate">{c.title} <span className="text-fg-4">· {c.source}</span></li>)}
            </ul>
          ) : <div className="text-fg-4">Nenhum trecho relevante</div>}
        </>
      );
    case 'llm_call':
      return <div className="text-fg-3"><span className="font-medium text-fg-2">Modelo</span> {e.provider === 'scripted' || e.simulated ? 'simulação' : (e.model ?? 'IA')} · {PROFILE_LABEL[e.profile] ?? e.profile} · {fmt.int(e.inputTokens)}/{fmt.int(e.outputTokens)} tokens{e.latencyMs ? ` · ${fmt.ms(e.latencyMs)}` : ''}</div>;
    case 'tool':
      return (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-fg">{e.name}</span>
            <StatusDot tone={DECISION_TONE[e.decision] ?? 'muted'} className="text-xs">{DECISION_LABEL[e.decision] ?? e.decision}</StatusDot>
          </div>
          {e.reason && <div className="mt-0.5 text-fg-3">motivo: {e.reason}</div>}
          {Object.keys(e.args).length > 0 && <div className="mt-0.5 truncate font-mono text-2xs text-fg-4">{JSON.stringify(e.args)}</div>}
        </>
      );
    case 'profile_change':
      return <div className="text-fg-3"><span className="font-medium text-fg-2">Encaminhado</span> {PROFILE_LABEL[e.from]} → {PROFILE_LABEL[e.to]}</div>;
    case 'ai_failover':
      return <div className="text-warning">Serviço de IA alternativo acionado automaticamente</div>;
    case 'model_substituted':
      return <div className="text-warning"><span className="font-medium">Modelo substituído</span> {e.from} → {e.to} <span className="text-fg-3">(retirado pelo fornecedor; substituto da lista oficial)</span></div>;
    case 'model_fallback':
      return <div className="text-warning"><span className="font-medium">Reserva acionada</span> {e.from} → {e.to} <span className="text-fg-3">({FALLBACK_REASON[e.reason] ?? e.reason})</span></div>;
    case 'handoff':
      return <div className="text-warning">Transferido para humano · {e.reason}</div>;
    case 'guard':
      return <div className="text-danger">Filtro de saída: {e.action} · {e.reason}</div>;
    case 'error':
      return <div className="text-danger">Erro: {e.message}</div>;
  }
}
